/**
 * Validation for an add-on selection against the groups a product offers.
 *
 * Shared deliberately: the PDP uses it to decide whether add-to-cart is
 * allowed and what to tell the shopper, and checkout uses the same function to
 * decide whether to accept the order. One implementation means the storefront
 * can't disagree with the server about what a valid selection is — and the
 * server never relies on the client having checked.
 */

export type AddonGroupRules = {
  id: string;
  is_required: boolean;
  min_select: number;
  max_select: number;
  /** Ids of the items this group offers. */
  itemIds: string[];
};

export type SelectionViolation =
  | { reason: 'too_few'; groupId: string; min: number }
  | { reason: 'too_many'; groupId: string; max: number }
  | { reason: 'unknown_item'; groupId: string; itemId: string };

/**
 * `max_select` of 0 means "no ceiling" — the admin form treats an unset
 * maximum that way, and a literal 0 would otherwise forbid every selection in
 * a group the merchant just meant to leave open.
 */
function ceilingFor(group: AddonGroupRules): number {
  return group.max_select > 0 ? group.max_select : Number.POSITIVE_INFINITY;
}

/**
 * Every rule the selection breaks. Empty array means it is valid.
 *
 * Selections naming a group the product does not offer are reported as
 * `unknown_item`: from the product's point of view that item does not exist,
 * and treating it as merely "extra" would let a caller price an add-on from
 * somebody else's product.
 */
export function validateAddonSelection(
  groups: readonly AddonGroupRules[],
  selection: readonly { group_id: string; item_id: string }[],
): SelectionViolation[] {
  const violations: SelectionViolation[] = [];
  const byGroup = new Map(groups.map((g) => [g.id, g]));

  for (const picked of selection) {
    const group = byGroup.get(picked.group_id);
    if (!group || !group.itemIds.includes(picked.item_id)) {
      violations.push({
        reason: 'unknown_item',
        groupId: picked.group_id,
        itemId: picked.item_id,
      });
    }
  }

  for (const group of groups) {
    const chosen = selection.filter(
      (s) => s.group_id === group.id && group.itemIds.includes(s.item_id),
    ).length;
    const min = group.is_required ? Math.max(1, group.min_select) : group.min_select;
    if (chosen < min) {
      violations.push({ reason: 'too_few', groupId: group.id, min });
    }
    const max = ceilingFor(group);
    if (chosen > max) {
      violations.push({ reason: 'too_many', groupId: group.id, max });
    }
  }

  return violations;
}

/** True when the selection satisfies every group's rules. */
export function isAddonSelectionValid(
  groups: readonly AddonGroupRules[],
  selection: readonly { group_id: string; item_id: string }[],
): boolean {
  return validateAddonSelection(groups, selection).length === 0;
}
